import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import Button from '@mui/material/Button';
import { fromInvoices, useAppDispatch } from '../../store';
import { fromOrder } from '../../store/rootReducer';
import Forbidden from '../forbidden';
import OrderTable from './OrderTable';
import { useQuery } from '../../routes';
import ButtonToSubscriptionManagement from './ButtonToSubscriptionManagement';
import ButtonToLicenseManagement from './ButtonToLicenseManagement';
import { LoadingProgress } from '../../layout';
import {
  getFeatureFromPlan,
  Invoice,
  InvoiceState,
  isTruePlatformFeature,
  OrderStatus,
  SerializedException,
} from '../../models';
import InvoiceTable from './InvoiceTable';
import {
  GET_INVOICE_CANNOT_READ_RECURLY_SUBSCRIPTION,
  GET_INVOICE_RECURLY_SUBSCRIPTION_NOT_EXISTED,
} from '../../store/invoiceSlice';
import ButtonToOrderConfirmation from './ButtonToOrderConfirmation';
import {
  STATUS_POLL_INTERVAL_MS,
  statusPollDecisionForFailure,
  statusPollDecisionForSuccess,
} from './paymentStatusPolling';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    backgroundColor: '#F7F9F8',
  },
  container: {
    width: '100%',
    maxWidth: 1080,
    margin: '0 auto',
    padding: theme.spacing(5, 4, 10.5),
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  card: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E1E7E4',
    borderRadius: 16,
  },
  statusCard: {
    padding: '48px 40px',
  },
  statusInner: {
    maxWidth: 660,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  statusIconCircle: {
    width: 88,
    height: 88,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
  },
  statusMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  statusTitle: {
    margin: '24px 0 0',
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 32,
    lineHeight: 1.15,
    fontWeight: 600,
    letterSpacing: '-.02em',
    color: '#16141D',
  },
  statusDescription: {
    margin: '13px 0 0',
    fontSize: 15.5,
    lineHeight: 1.6,
    color: '#5F5D75',
  },
  actions: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: 28,
  },
  summaryCard: {
    padding: '26px 30px 30px',
  },
  summaryHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  summaryTitle: {
    margin: 0,
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '-.01em',
    color: '#16141D',
  },
  summaryOrderNumber: {
    fontSize: 14,
    fontWeight: 700,
    color: '#16141D',
  },
  summaryOrderDate: {
    fontSize: 12.5,
    color: '#9A98AD',
  },
}));

const PaymentStatus = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { get, getAll } = useQuery();
  const orderId = get('orderId');
  const order = useSelector(fromOrder.selectOrderResponse);
  const invoiceNumbers = Array.from(new Set(getAll('invoiceNumber')));
  const [loading, setLoading] = useState(true);
  const invoices = (useSelector(
    fromInvoices.selectInvoiceByNumbers([...invoiceNumbers]),
  ).filter(it => it!!) as Invoice[]);
  const [isForbiddenError, setIsForbiddenError] = useState(false);
  const [statusCheckFailed, setStatusCheckFailed] = useState(false);
  const [statusCheckLoading, setStatusCheckLoading] = useState(false);
  const errors = useSelector(fromInvoices.selectErrors());

  const singleProductInvoices = invoices
    .filter(invoice => invoiceNumbers.includes(invoice?.invoiceNumber!!))
    .sort((a, b) => {
      if (a?.state! > b?.state!) return -1;
      if (a?.state! < b?.state!) return 1;
      return 0;
    });

  const isTrue = order?.orderItems?.some(item => {
    const feature = getFeatureFromPlan(item.planId);
    if (feature) {
      return isTruePlatformFeature(feature);
    }
    return false;
  });

  const failedInvoices: Array<string> = useMemo(() => invoices
    .filter(it => it?.state === InvoiceState.FAILED)
    .map(invoice => invoice.invoiceNumber!!), [invoices]);

  const pendingInvoices: Array<string> = useMemo(() => {
    if (invoices.length === 0) {
      return invoiceNumbers;
    }
    return invoices
      .filter(it => it?.state !== InvoiceState.FAILED && it?.state !== InvoiceState.PAID)
      .map(invoice => invoice.invoiceNumber!!);
  }, [invoices]);
  // Redux error updates can rerender this page without changing the pending
  // invoice set. Depend on its value, not the freshly allocated array, so one
  // poll completion cannot create a second timer for the same interval.
  const pendingInvoiceNumbersKey = pendingInvoices.join('\u0000');

  // Terminal statuses stop the poll — a canceled order will never move again.
  const terminalOrderStatuses = [OrderStatus.PAID, OrderStatus.FAIL, OrderStatus.CANCEL];
  const hasPendingStatus = pendingInvoices.length > 0
    || (orderId !== null
      && (order?.status === undefined || !terminalOrderStatuses.includes(order.status))
    );
  const shouldPoll = hasPendingStatus && !statusCheckFailed;

  // Hero state per Billing 2.0 design: processing while the poll is still
  // running, failed when the order/any invoice ended in failure, success otherwise.
  const isFailedState = !hasPendingStatus
    && (failedInvoices.length > 0
      || order?.status === OrderStatus.FAIL
      || order?.status === OrderStatus.CANCEL);
  const isSuccessState = !hasPendingStatus && !isFailedState;
  const canRetryPayment = failedInvoices.length > 0 || order?.status === OrderStatus.FAIL;

  const timeoutRef = useRef<NodeJS.Timeout>();
  // Only show the loading overlay on the very first fetch; subsequent status
  // polls run silently in the background so the UI doesn't flash every few seconds.
  const isFirstLoadRef = useRef(true);
  const consecutiveFailuresRef = useRef(0);

  const dispatchStatusRead = () => (orderId
    ? dispatch(fromOrder.doFetchOrderById({
      orderId: Number(orderId),
      errorPresentation: 'caller',
    }))
    : dispatch(fromInvoices.doGetInvoiceByInvoiceNumbers({
      invoiceNumbers: pendingInvoices,
      errorPresentation: 'caller',
    })));

  useEffect(() => {
    if (!shouldPoll) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      setLoading(false);
      return undefined;
    }

    // clearTimeout alone can't stop a poll whose fetch is in flight when the
    // effect is cleaned up: the await resolves afterwards and would schedule a
    // fresh timeout that nothing clears anymore (polling then survives page
    // navigation). The flag makes the resolved fetch bail out instead.
    let cancelled = false;

    const fetchData = async () => {
      if (isFirstLoadRef.current) {
        setLoading(true);
      }
      const action = await dispatchStatusRead();
      if (cancelled) {
        return;
      }
      const decision = action.meta.requestStatus === 'rejected'
        ? statusPollDecisionForFailure(
          action.payload as SerializedException | undefined,
          consecutiveFailuresRef.current,
        )
        : statusPollDecisionForSuccess();
      consecutiveFailuresRef.current = decision.consecutiveFailures;
      if (isFirstLoadRef.current) {
        setLoading(false);
        isFirstLoadRef.current = false;
      }

      if (decision.stop) {
        setStatusCheckFailed(true);
        setLoading(false);
        return;
      }

      // Schedule next poll
      timeoutRef.current = setTimeout(() => {
        fetchData().catch();
      }, decision.delayMs);
    };

    // Start polling
    timeoutRef.current = setTimeout(() => {
      fetchData().catch();
    }, STATUS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };
  }, [order?.status, orderId, pendingInvoiceNumbersKey, dispatch, shouldPoll]);

  const handleRetryStatusCheck = async () => {
    setStatusCheckLoading(true);
    const action = await dispatchStatusRead();
    if (action.meta.requestStatus === 'fulfilled') {
      consecutiveFailuresRef.current = 0;
      setStatusCheckFailed(false);
    }
    setStatusCheckLoading(false);
  };

  useEffect(() => {
    if (errors.length === 0 || !errors[0]) {
      return;
    }

    const { message } = errors[0];
    switch (message) {
      case GET_INVOICE_RECURLY_SUBSCRIPTION_NOT_EXISTED:
        enqueueSnackbar(
          <FormattedMessage id="invoice_recurly_subscription_not_existed" />,
          {
            variant: 'error',
            preventDuplicate: true,
            key: intl.formatMessage({ id: 'invoice_recurly_subscription_not_existed' }),
            autoHideDuration: 2000,
            resumeHideDuration: 0,
          },
        );
        dispatch(fromInvoices.doResetErrors());
        setIsForbiddenError(true);
        break;
      case GET_INVOICE_CANNOT_READ_RECURLY_SUBSCRIPTION:
        enqueueSnackbar(
          <FormattedMessage id="invoice_cannot_read_recurly_subscription" />,
          {
            variant: 'error',
            preventDuplicate: true,
            key: intl.formatMessage({ id: 'invoice_cannot_read_recurly_subscription' }),
            autoHideDuration: 2000,
            resumeHideDuration: 0,
          },
        );
        dispatch(fromInvoices.doResetErrors());
        setIsForbiddenError(true);
        break;
      default:
        break;
    }
  }, [errors]);

  // if error code is permission errors -> 403 page but with the sidebars and stuff.
  if (isForbiddenError) {
    return <Forbidden />;
  }

  const renderStatusHero = () => {
    if (statusCheckFailed) {
      return (
        <div className={classes.statusMessage} role="status" aria-live="polite">
          <span
            className={classes.statusIconCircle}
            style={{ backgroundColor: '#FFF4E5', border: '1px solid #FFD9A8' }}
          >
            <ScheduleOutlinedIcon sx={{ fontSize: 46, color: '#B54708' }} />
          </span>
          <Typography variant="h2" className={classes.statusTitle}>
            <FormattedMessage id="payment_status.status_unavailable.title" />
          </Typography>
          <Typography className={classes.statusDescription}>
            <FormattedMessage id="payment_status.status_unavailable.description" />
          </Typography>
        </div>
      );
    }
    if (isFailedState) {
      return (
        <>
          <span
            className={classes.statusIconCircle}
            style={{ backgroundColor: '#FDE7E7', border: '1px solid #FBCFCF' }}
          >
            <HighlightOffIcon sx={{ fontSize: 46, color: '#D14343' }} />
          </span>
          <Typography variant="h2" className={classes.statusTitle}>
            <FormattedMessage id="payment_status.hero.failed.title" />
          </Typography>
          <Typography className={classes.statusDescription}>
            <FormattedMessage id="payment_status.hero.failed.description" />
          </Typography>
        </>
      );
    }
    if (isSuccessState) {
      return (
        <>
          <span
            className={classes.statusIconCircle}
            style={{ backgroundColor: '#EDF8F3', border: '1px solid #CFEADD' }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 46, color: '#0F8461' }} />
          </span>
          <Typography variant="h2" className={classes.statusTitle}>
            <FormattedMessage id="payment_status.hero.success.title" />
          </Typography>
          <Typography className={classes.statusDescription}>
            <FormattedMessage id="payment_status.hero.success.description" />
          </Typography>
        </>
      );
    }
    return (
      <>
        <span
          className={classes.statusIconCircle}
          style={{ backgroundColor: '#EEF2F8', border: '1px solid #D6DEF0' }}
        >
          <CircularProgress size={40} thickness={3.6} sx={{ color: '#3A4A63' }} />
        </span>
        <Typography variant="h2" className={classes.statusTitle}>
          <FormattedMessage id="payment_status.hero.processing.title" />
        </Typography>
        <Typography className={classes.statusDescription}>
          <FormattedMessage id="payment_status.hero.processing.description" />
        </Typography>
      </>
    );
  };

  return (
    <Box id="payment-status" className={classes.root}>
      <div className={classes.container}>
        <section id="payment-status-panel" className={`${classes.card} ${classes.statusCard}`}>
          <div id="payment-status-container" className={classes.statusInner}>
            {renderStatusHero()}
            {!shouldPoll && (
              <div className={classes.actions}>
                {statusCheckFailed && (
                  <Button
                    variant="contained"
                    disabled={statusCheckLoading}
                    onClick={() => handleRetryStatusCheck().catch()}
                  >
                    <FormattedMessage
                      id={statusCheckLoading
                        ? 'payment_status.status_unavailable.checking'
                        : 'payment_status.status_unavailable.check'}
                    />
                  </Button>
                )}
                {canRetryPayment && <ButtonToOrderConfirmation />}
                {isSuccessState && (
                  <ButtonToLicenseManagement
                    title={isTrue ? 'go.to.true.platform' : 'payment_status.button.back_license_management'}
                    width="auto"
                    height={46}
                    fontWeight={600}
                    fontSize={14}
                    borderRadius="10px"
                    paddingX="22px"
                    disableTextTransform
                    backgroundColor="#0F8461"
                    hoverBackgroundColor="#0B6B4E"
                  />
                )}
                <ButtonToSubscriptionManagement />
              </div>
            )}
          </div>
        </section>
        <section id="order-summary-panel" className={`${classes.card} ${classes.summaryCard}`}>
          <div className={classes.summaryHeader}>
            <Typography variant="h3" className={classes.summaryTitle}>
              <FormattedMessage id="payment_success.order_summary.title" />
            </Typography>
            {order && orderId && (
              <Box textAlign="right" lineHeight={1.35}>
                <Typography className={classes.summaryOrderNumber}>
                  {intl.formatMessage(
                    { id: 'payment_success.order_summary.order_number' },
                    { orderId: order.id },
                  )}
                </Typography>
                <Typography className={classes.summaryOrderDate}>
                  {intl.formatMessage(
                    { id: 'payment_success.order_summary.order_placed' },
                    {
                      date: intl.formatDate(new Date(order.orderDate), {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                      }),
                    },
                  )}
                </Typography>
              </Box>
            )}
          </div>
          <Box marginTop={2.5}>
            {orderId
              ? (
                <OrderTable order={order} />
              )
              : (
                <InvoiceTable
                  singleProductInvoices={singleProductInvoices}
                />
              )}
          </Box>
        </section>
      </div>
      {loading && <LoadingProgress fullscreen />}
    </Box>
  );
};

export default PaymentStatus;
